import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play,
  Download,
  Layers,
  Grid3x3,
  RefreshCw,
  Sparkles,
  Scissors,
  Mic,
  Youtube,
  Instagram,
  Gamepad2,
  Film,
  BookOpen,
  Scale,
  Star,
  Check,
  ArrowRight,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import "../styles/landing.css";
import editorScreenImage from "../assets/editor_screen.png";
import galleryScreenImage from "../assets/gallery_screen.png";
import homeScreenImage from "../assets/home_screen.png";
import recordScreenImage from "../assets/record_screen.png";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const appScreenshots = [
    {
      url: homeScreenImage,
      title: "Creator Tools Home",
      description: "Easy access to reaction video and background removal tools",
    },
    {
      url: galleryScreenImage,
      title: "Your Video Gallery",
      description: "Organize and manage all your reaction videos",
    },
    {
      url: recordScreenImage,
      title: "Live Recording",
      description: "Record real-time reactions with perfect sync",
    },
    {
      url: editorScreenImage,
      title: "Professional Editor",
      description: "Full-featured editor with timeline and effects",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [appScreenshots.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length,
    );
  };

  const playStoreLink =
    "https://play.google.com/store/apps/details?id=com.corvas.reactioncam";
  const youtubeDemoLink = "https://youtu.be/dii5eHZDyWM?si=ZmqV7np0WGaUhDL3";

  const features = [
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Picture-in-Picture (PiP) Recording",
      description:
        "Add your facecam overlay on any video with perfect positioning and resizing controls.",
    },
    {
      icon: <Grid3x3 className="w-8 h-8" />,
      title: "Side-by-Side & Top-Bottom Layouts",
      description:
        "Choose from multiple layout options for your reaction videos with professional split-screen views.",
    },
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Auto-Sync Video Playback",
      description:
        "Perfectly synchronized playback ensures your reactions match the content timing.",
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "HD Export",
      description:
        "Export your reaction videos in high-definition quality ready for any platform.",
    },
    {
      icon: <Scissors className="w-8 h-8" />,
      title: "Background Removal Tool",
      description:
        "Remove backgrounds from your facecam for a clean, professional look.",
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Audio Recording & Commentary",
      description:
        "Record crystal-clear audio commentary and adjust volume levels with ease.",
    },
  ];

  const useCases = [
    {
      icon: <Youtube className="w-12 h-12" />,
      title: "YouTube Reaction Videos",
      description:
        "Create engaging reaction content for your YouTube channel with professional layouts.",
    },
    {
      icon: <Instagram className="w-12 h-12" />,
      title: "TikTok & Instagram Reels",
      description:
        "Make viral-worthy short-form reaction content for social media platforms.",
    },
    {
      icon: <Gamepad2 className="w-12 h-12" />,
      title: "Gaming Reactions",
      description:
        "React to gameplay, walkthroughs, and gaming content with facecam overlays.",
    },
    {
      icon: <Film className="w-12 h-12" />,
      title: "Movie & Music Reviews",
      description:
        "Share your thoughts on movies, shows, and music with picture-in-picture reactions.",
    },
    {
      icon: <BookOpen className="w-12 h-12" />,
      title: "Educational Commentary",
      description:
        "Add your expert commentary to educational videos and tutorials.",
    },
    {
      icon: <Scale className="w-12 h-12" />,
      title: "Comparison & Review Videos",
      description:
        "Create detailed product comparisons and reviews with professional overlays.",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Import a Video",
      description: "Choose any video from your gallery or record your screen.",
    },
    {
      number: "02",
      title: "Record Your Reaction",
      description: "Hit record and react naturally while the video plays.",
    },
    {
      number: "03",
      title: "Export & Share",
      description:
        "Export in HD and share directly to your favorite platforms.",
    },
  ];

  const whyChoose = [
    {
      icon: <Check className="w-6 h-6" />,
      text: "Beginner Friendly Interface",
    },
    {
      icon: <Check className="w-6 h-6" />,
      text: "No Complicated Editing Required",
    },
    { icon: <Check className="w-6 h-6" />, text: "Fast Processing & Export" },
    { icon: <Check className="w-6 h-6" />, text: "Clean & Intuitive UI" },
    {
      icon: <Check className="w-6 h-6" />,
      text: "Professional Results Every Time",
    },
  ];

  return (
    <div className="landing-container">
      {/* Header */}
      <header className={`dark-header ${scrolled ? "scrolled" : ""}`}>
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-text">ReactionCam</div>
          </div>

          <nav className={`dark-nav ${mobileMenuOpen ? "mobile-open" : ""}`}>
            <a
              href="#features"
              className="dark-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="dark-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works
            </a>
            <a
              href="#use-cases"
              className="dark-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Use Cases
            </a>
            <Button
              className="btn-primary header-cta"
              onClick={() => window.open(playStoreLink, "_blank")}
            >
              Download <Download className="w-5 h-5 ml-2" />
            </Button>
          </nav>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-gradient"></div>
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <Sparkles className="w-4 h-4" />
              <span>The Ultimate Reaction Video Studio</span>
            </div>
            <h1 className="hero-headline">
              Create{" "}
              <span className="gradient-text">Stunning Reaction Videos</span> in
              Minutes
            </h1>
            <p className="hero-subheadline">
              Professional picture-in-picture, split-screen layouts, and
              auto-sync technology. Everything you need to become a top content
              creator.
            </p>
            <div className="hero-cta-group">
              <Button
                className="btn-primary btn-large neon-glow"
                onClick={() => window.open(playStoreLink, "_blank")}
              >
                Download on Google Play <Download className="w-5 h-5 ml-2" />
              </Button>
              <Button
                className="btn-secondary btn-large"
                onClick={() => window.open(youtubeDemoLink, "_blank")}
              >
                Watch Demo <Play className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="hero-badges">
              <div className="badge-item">
                <div className="badge-icon">
                  <Star className="w-5 h-5" fill="currentColor" />
                </div>
                <span>4.4 Rating</span>
              </div>
              <div className="badge-item">
                <div className="badge-icon">
                  <Download className="w-5 h-5" />
                </div>
                <span>5,000+ Downloads</span>
              </div>
              <div className="badge-item">
                <div className="badge-icon">
                  <Youtube className="w-5 h-5" />
                </div>
                <span>Trusted by Creators</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-image-wrapper">
              <div className="neon-frame">
                <div className="floating-card card-1">
                  <Layers className="w-6 h-6" />
                  <span>PiP Recording</span>
                </div>
                <data className="floating-card card-2">
                  <Grid3x3 className="w-6 h-6" />
                  <span>Split-Screen Layouts</span>
                </data>
                <div className="floating-card card-3">
                  <RefreshCw className="w-6 h-6" />
                  <span>Auto-Sync Playback</span>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1627244714766-94dab62ed964"
                  alt="Content Creator with ReactionCam"
                  className="hero-banner-img"
                />
              </div>
              {/* <div className="floating-orb orb-1"></div>
              <div className="floating-orb orb-2"></div>
              <div className="floating-orb orb-3"></div> */}
            </div>
          </div>
        </div>
      </section>

      {/* App Screenshots Showcase */}
      <section className="app-showcase-section">
        <div className="section-header">
          <div className="section-badge">
            <Layers className="w-4 h-4" />
            <span>App Screenshots</span>
          </div>
          <h2 className="section-title">
            Experience the <span className="gradient-text">Power</span> of
            ReactionCam
          </h2>
          <p className="section-subtitle">
            Intuitive interface designed for professional creators
          </p>
        </div>

        <div className="carousel-container">
          <div className="carousel-wrapper">
            {appScreenshots.map((screenshot, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === currentSlide ? "active" : ""}`}
                style={{
                  transform: `translateX(${(index - currentSlide) * 100}%)`,
                  opacity: index === currentSlide ? 1 : 0.7,
                  pointerEvents: index === currentSlide ? "auto" : "none",
                }}
              >
                <div className="screenshot-display">
                  <div className="screenshot-glow"></div>
                  <img
                    src={screenshot.url}
                    alt={screenshot.title}
                    className="screenshot-img-main"
                  />
                </div>
                <div className="screenshot-info">
                  <h3>{screenshot.title}</h3>
                  <p>{screenshot.description}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            className="carousel-btn carousel-btn-prev"
            onClick={prevSlide}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="carousel-btn carousel-btn-next"
            onClick={nextSlide}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="carousel-dots">
            {appScreenshots.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentSlide ? "active" : ""}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <div className="section-badge">
            <Sparkles className="w-4 h-4" />
            <span>Features</span>
          </div>
          <h2 className="section-title">
            Powerful <span className="gradient-text">Creator Tools</span>
          </h2>
          <p className="section-subtitle">
            Everything you need to create professional reaction videos
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card neon-border">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">{feature.icon}</div>
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-header">
          <div className="section-badge">
            <Play className="w-4 h-4" />
            <span>Simple Process</span>
          </div>
          <h2 className="section-title">
            Create in <span className="gradient-text">3 Easy Steps</span>
          </h2>
          <p className="section-subtitle">
            Start creating professional reaction videos in minutes
          </p>
        </div>
        <div className="steps-container">
          {steps.map((step, index) => (
            <div key={index} className="step-card neon-border">
              <div className="step-number-wrapper">
                <div className="step-number">{step.number}</div>
              </div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
              {index < steps.length - 1 && (
                <div className="step-connector">
                  <ArrowRight className="step-arrow" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="use-cases-section">
        <div className="section-header">
          <div className="section-badge">
            <Film className="w-4 h-4" />
            <span>Use Cases</span>
          </div>
          <h2 className="section-title">
            Perfect for <span className="gradient-text">Every Creator</span>
          </h2>
          <p className="section-subtitle">
            Whether you're a YouTuber, student, or social media creator
          </p>
        </div>
        <div className="use-cases-grid">
          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-card neon-border">
              <div className="use-case-icon-wrapper">
                <div className="use-case-icon">{useCase.icon}</div>
              </div>
              <h3 className="use-case-title">{useCase.title}</h3>
              <p className="use-case-description">{useCase.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section">
        <div className="why-choose-content">
          <div className="why-choose-text">
            <h2 className="section-title">
              Why Choose <span className="gradient-text">ReactionCam?</span>
            </h2>
            <p className="section-subtitle">
              The simplest way to create professional reaction videos
            </p>
            <div className="why-choose-list">
              {whyChoose.map((item, index) => (
                <div key={index} className="why-choose-item">
                  <div className="check-icon">{item.icon}</div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="why-choose-images">
            <div className="why-choose-image-main">
              <img
                src="https://images.unsplash.com/photo-1622513347447-d6c1555e22e4"
                alt="Content Creator Setup"
                className="creator-image"
              />
            </div>
            <div className="why-choose-image-grid">
              <img
                src="https://images.unsplash.com/photo-1636293875439-b3125c0f1fc1"
                alt="Studio Setup"
                className="creator-image-small"
              />
              <img
                src="https://images.unsplash.com/photo-1698945298361-365595d400eb"
                alt="Creator Workspace"
                className="creator-image-small"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section">
        <div className="social-proof-content">
          <h2 className="section-title">
            <span className="gradient-text">Loved by Creators</span> Worldwide
          </h2>
          <div className="rating-display">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8" fill="currentColor" />
              ))}
            </div>
            <p className="rating-text">4.4 Stars on Google Play (190 reviews)</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-header">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-bold text-lg border border-cyan-500/30">
                  KA
                </div>
                <div>
                  <p className="testimonial-author">Kajal Acharya</p>
                  <p className="testimonial-role">Google Play Reviewer ★★★★★</p>
                </div>
              </div>
              <p className="testimonial-text">
                "I absolutely love ReactionCam! I can create reaction videos without any editing skills. Just select a video, record my reaction and it's done. It's super easy, smooth and beginner friendly. Perfect for reaction videos, reels and social media."
              </p>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-header">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-bold text-lg border border-cyan-500/30">
                  SA
                </div>
                <div>
                  <p className="testimonial-author">Sipra Acharya</p>
                  <p className="testimonial-role">Google Play Reviewer ★★★★★</p>
                </div>
              </div>
              <p className="testimonial-text">
                "As a homemaker, I love using the app to record my reactions to cooking videos. The app is very easy to use, works smoothly, and helps me create beautiful reaction videos without any editing knowledge. Highly recommend!"
              </p>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-header">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-bold text-lg border border-cyan-500/30">
                  TO
                </div>
                <div>
                  <p className="testimonial-author">Tochukwu Ojide</p>
                  <p className="testimonial-role">Google Play Reviewer ★★★★★</p>
                </div>
              </div>
              <p className="testimonial-text">
                "Great app. Simple and very easy to use. No complicated setup, just pick your source video and react."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="cta-bg-gradient"></div>
        <div className="cta-content">
          <div className="section-badge cta-badge">
            <Download className="w-4 h-4" />
            <span>Get Started</span>
          </div>
          <h2 className="cta-title">
            Start Creating{" "}
            <span className="gradient-text">Amazing Content</span>
          </h2>
          <p className="cta-subtitle">
            Join thousands of creators making professional reaction content
          </p>
          <Button
            className="btn-primary btn-xl neon-glow-strong"
            onClick={() => window.open(playStoreLink, "_blank")}
          >
            Download ReactionCam <Download className="w-6 h-6 ml-2" />
          </Button>
          <div className="google-play-badge">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              className="play-badge-img"
              onClick={() => window.open(playStoreLink, "_blank")}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="dark-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-logo">ReactionCam</h3>
            <p className="footer-description">
              The simplest way to create professional reaction videos for
              YouTube, TikTok, and more.
            </p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Product</h4>
            <a href="#features" className="footer-link">
              Features
            </a>
            <a href="#how-it-works" className="footer-link">
              How It Works
            </a>
            <a href="#use-cases" className="footer-link">
              Use Cases
            </a>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Company</h4>
            <Link to="/privacy" className="footer-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="footer-link">
              Terms of Use
            </Link>
            <a href="mailto:corvas.official@gmail.com" className="footer-link">
              Support
            </a>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">More Apps</h4>
            <a
              href="https://play.google.com/store/apps/details?id=com.corvas.pencilate"
              className="footer-link"
            >
              Pencilate
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.corvas.behindtext"
              className="footer-link"
            >
              BehindText
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 ReactionCam. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
